/**
 * 服务时间选择器（从 BLW expert-time-comp.vue 迁移）
 *
 * 功能：
 * 1. 调用 GET /api/massage/book_time 获取达人可预约时间段
 * 2. 展示多天日期 + 每天的时间段网格
 * 3. 独立多选模式（点击选中/取消，每个时间段 toggle）
 * 4. 小时单最少选 minHour 小时
 * 5. 确认后 emit (showDesc, book_time, book_end_time, num)
 */
import { useState, useEffect, useRef, useMemo } from "react";
import dayjs from "dayjs";

import { getBookTime } from "@/api/expert-order";
import TimeItem from "./TimeItem";
import type {
  ExpertBookTimeDataItem,
  ExpertBookTimeListItem,
  TimeConfirmParams,
} from "../types";

const PRIMARY = "#FAA300";

interface Props {
  staffId: number | string;
  itemId: number | string;
  /** 选择方式: 1=小时单 2=日单 */
  chooseType: number;
  /** 最少选择几小时（仅小时单生效） */
  minHour?: number;
  /** 缓存的已选开始时间（用于切换回来时复显） */
  cachedBookTime?: number;
  /** 缓存的已选数量 */
  cachedNum?: number;
  onConfirm: (params: TimeConfirmParams) => void;
}

/**
 * 格式化时间戳为显示文本
 * @param format 格式模板
 * @param timestamp 秒级时间戳 ×1000 转 ms
 */
const formatTime = (format: string, timestamp: number): string => {
  return dayjs(timestamp).format(format);
};

export default function ExpertTimeComp({
  staffId,
  itemId,
  chooseType,
  minHour = 3,
  cachedBookTime = 0,
  cachedNum = 1,
  onConfirm,
}: Props) {
  const [dateLists, setDateLists] = useState<ExpertBookTimeDataItem[]>([]);
  const floatTimeItemArrRef = useRef<ExpertBookTimeListItem[]>([]);

  // 独立多选：记录所有被选中的时间戳（每个时间段独立 toggle）
  const [selectedTimes, setSelectedTimes] = useState<number[]>([]);

  // ── 加载可预约时间段 ──
  const fetchBookTime = async () => {
    try {
      const data = await getBookTime({
        staff_id: Number(staffId),
        item_id: Number(itemId),
        accept_order_type: chooseType,
      });

      const flatList: ExpertBookTimeListItem[] = [];

      const processed = data.map((ele) => {
        const list = ele.list.map((item) => ({
          ...item,
          fullTime: ele.timestamp + item.timestamp as number,
          isSelected: false,
        }));
        flatList.push(...list);
        return { ...ele, list };
      });

      floatTimeItemArrRef.current = flatList;
      setDateLists(processed);

      // 尝试复显之前选择的时间
      restoreCachedSelection(flatList);
    } catch (err) {
      console.error("[时间选择] book_time 请求失败:", err);
    }
  };

  // 复显缓存的时间选择
  const restoreCachedSelection = (flatList: ExpertBookTimeListItem[]) => {
    const cachedStart = Number(cachedBookTime);
    const cachedN = Number(cachedNum);
    if (cachedStart <= 0 || cachedN < 1) return;

    const startIdx = flatList.findIndex((e) => e.fullTime === cachedStart);
    if (startIdx === -1) return;

    const endIdx = startIdx + cachedN - 1;
    if (endIdx >= flatList.length) return;

    const rangeSlots = flatList.slice(startIdx, endIdx + 1);
    if (rangeSlots.some((e) => !e.select)) {
      console.warn("[时间选择] 缓存时段已被占用，需要重新选择");
      setSelectedTimes([]);
      return;
    }

    // 取出范围内所有时间戳作为选中集合
    const cachedTimes = rangeSlots.map((e) => e.fullTime!).filter(Boolean);
    setSelectedTimes(cachedTimes);

    // 标记 isSelected
    const updated = flatList.map((e) => ({
      ...e,
      isChecked: cachedTimes.includes(e.fullTime!),
    }));
    floatTimeItemArrRef.current = updated;
    setDateLists((prev) =>
      prev.map((day) => ({
        ...day,
        list: day.list.map((slot) => {
          const found = updated.find((u) => u.timestamp === slot.timestamp);
          return found ? { ...slot, isSelected: !!found?.isChecked } : slot;
        }),
      })),
    );
  };

  // ── 点击选择/取消时间段（toggle 模式） ──
  const handleChoose = (clickedItem: ExpertBookTimeListItem) => {
    if (!clickedItem.select) return;

    const currentTimeStamp = clickedItem.fullTime!;

    setSelectedTimes((prev) => {
      if (prev.includes(currentTimeStamp)) {
        // 已选中 → 取消该时间
        return prev.filter((t) => t !== currentTimeStamp);
      } else {
        // 未选中 → 添加到选中列表（保持排序）
        return [...prev, currentTimeStamp].sort((a, b) => a - b);
      }
    });
  };

  // 根据 selectedTimes 同步更新 dateLists 中每个 slot 的 isSelected 状态
  useEffect(() => {
    if (dateLists.length === 0) return;

    const timeSet = new Set(selectedTimes);
    setDateLists((prev) =>
      prev.map((day) => ({
        ...day,
        list: day.list.map((slot) => ({
          ...slot,
          isSelected: timeSet.has(slot.fullTime!),
        })),
      })),
    );
  }, [selectedTimes]);

  // ── 计算属性 ──
  const selectedTotal = useMemo(() => {
    return dateLists.reduce(
      (sum, day) => sum + day.list.filter((s) => s.isSelected).length,
      0,
    );
  }, [dateLists]);

  const selectedTips = `${selectedTotal}${chooseType === 1 ? "小时" : "天"}`;

  // ── 确认按钮 ──
  const handleConfirm = () => {
    if (selectedTimes.length === 0) {
      alert("请选择预约的时间");
      return;
    }

    if (chooseType === 1 && selectedTotal < minHour) {
      alert(`请选择${minHour}小时以上的预约时间`);
      return;
    }

    // 从已选时间中取最小和最大值作为起止时间
    const sorted = [...selectedTimes].sort((a, b) => a - b);
    const bookTime = sorted[0];
    const bookEndTime = sorted[sorted.length - 1];

    const format = chooseType === 1 ? "MM/DD HH:mm" : "YYYY-MM-DD";
    const params: TimeConfirmParams = {
      showDesc: `${formatTime(format, bookTime * 1000)}-${formatTime(format, bookEndTime * 1000)}(${selectedTips})`,
      book_time: bookTime,
      book_end_time: bookEndTime,
      num: selectedTotal,
    };
    onConfirm(params);
  };

  // 外部调用的重置方法（通过 ref 暴露）
  const initData = () => {
    setSelectedTimes([]);
    floatTimeItemArrRef.current = [];
    fetchBookTime();
  };

  // 参数变化时自动加载
  useEffect(() => {
    if (staffId !== -1 && chooseType !== -1) {
      fetchBookTime();
    }
  }, [staffId, itemId, chooseType]);

  // 暴露方法给父组件
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useImperativeHandle<any, { initData: () => void }>(
    useRef(null),
    () => ({ initData }),
  );

  return (
    <div style={{
      padding: 10,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      background: "#fff",
      maxHeight: "70vh",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* 标题 */}
      <div style={{
        fontWeight: 700, textAlign: "center", color: "#333", marginBottom: 15,
        flexShrink: 0,
      }}>
        选择服务时间
      </div>

      {/* 可滚动区域 */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 20 }}>
          {dateLists.map((item) => (
            <TimeItem
              key={item.timestamp}
              timeItem={item}
              currentSelectArr={[-1, -1]}
              onChoose={handleChoose}
            />
          ))}
        </div>
      </div>

      {/* 底部提示 + 按钮 */}
      <div style={{ padding: "0 10px", textAlign: "center", flexShrink: 0, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
        <span style={{ color: "#999", fontSize: 11 }}>
          实际到达时间可能会有30分钟的浮动(下单时间需三小时以上)
        </span>
        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
          {selectedTimes.length > 0 && (
            <button onClick={() => setSelectedTimes([])} style={{
              background: "#f5f5f5",
              fontWeight: 600, fontSize: 13, color: "#666",
              padding: "10px 0", borderRadius: 10,
              flex: "0 0 100px", border: "none",
              cursor: "pointer",
            }}>
              重新选择
            </button>
          )}
          <button onClick={handleConfirm} style={{
            background: PRIMARY,
            fontWeight: 700, fontSize: 13, color: "#fff",
            padding: "10px 0", borderRadius: 10,
            flex: 1, border: "none",
            cursor: "pointer",
          }}>
            {" "}确认选择{" "}
            {selectedTotal > 0 && (
              <span style={{ fontSize: 11 }}>（{selectedTips}）</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line react-hooks/rules-of-hooks
function useImperativeHandle<T, R>(_: T, fn: () => R): R {
  return fn();
}
